var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from 'react';
import { FaExclamationCircle, FaExclamationTriangle, FaInfoCircle, FaTerminal } from 'react-icons/fa';
import { VerificationCode, VerificationSeverity, MAP_IMAGE_PX } from '@common/contracts';
import { getMapNormalizeReport, runMapNormalize, runFixAll, } from '../../api/images';
import './InfoManager.scss';
const VERIFICATION_DOCS = {
    [VerificationCode.NoStart]: {
        severity: VerificationSeverity.Error,
        title: 'No start cell',
        meaning: 'The level has no cell marked as the Start — the player has nowhere to spawn.',
        fix: 'Assign the Start type to exactly one cell.',
    },
    [VerificationCode.MultiStart]: {
        severity: VerificationSeverity.Error,
        title: 'Several start cells',
        meaning: 'More than one cell is marked as the Start; the spawn point is ambiguous.',
        fix: 'Keep exactly one Start cell and clear the others.',
    },
    [VerificationCode.GoalUndefined]: {
        severity: VerificationSeverity.Error,
        title: 'No goal cell',
        meaning: 'No cell is marked as the goal, so the level has no defined win condition.',
        fix: 'Mark exactly one cell as the goal (the ⚑ flag on the map).',
    },
    [VerificationCode.MultiGoal]: {
        severity: VerificationSeverity.Error,
        title: 'Several goal cells',
        meaning: 'More than one cell is marked as the goal.',
        fix: 'Keep exactly one goal cell.',
    },
    [VerificationCode.GoalUnreachable]: {
        severity: VerificationSeverity.Error,
        title: 'Goal unreachable',
        meaning: 'No sequence of trial outcomes lets the player ever stand on the goal — the level cannot be finished.',
        fix: 'Check the locked doors on the way to the goal: some obstacle’s outcome must unlock each of them.',
    },
    [VerificationCode.GoalUnreachableOnSuccess]: {
        severity: VerificationSeverity.Error,
        title: 'Goal only reachable by failing',
        meaning: 'The goal can be reached, but only because a gate on the way opens on a FAILURE outcome. A player who succeeds at everything gets stuck.',
        fix: 'Make every gate on the path to the goal open on success, not on failure.',
    },
    [VerificationCode.UnlockTargetMissing]: {
        severity: VerificationSeverity.Error,
        title: 'Unlock target missing',
        meaning: 'An outcome tries to unlock a cell that does not exist or is a wall.',
        fix: 'Point the unlock at a real, non-wall cell (or remove the action).',
    },
    [VerificationCode.LockTargetMissing]: {
        severity: VerificationSeverity.Error,
        title: 'Lock target missing',
        meaning: 'An outcome tries to lock a cell that does not exist or is a wall.',
        fix: 'Point the lock at a real, non-wall cell (or remove the action).',
    },
    [VerificationCode.UnlockNeverFires]: {
        severity: VerificationSeverity.Error,
        title: 'Cell can never be unlocked',
        meaning: 'A cell starts locked and no obstacle anywhere can ever unlock it — its content is dead.',
        fix: 'Attach an unlock for this cell to some reachable obstacle, or start it unlocked.',
    },
    [VerificationCode.PanelMissing]: {
        severity: VerificationSeverity.Error,
        title: 'Panel does not exist',
        meaning: 'An outcome shows a notification panel by an id that no panel actually has.',
        fix: 'Point the “show panel” action at an existing panel, or create the panel.',
    },
    [VerificationCode.InconsistentOutcomeUnlocks]: {
        severity: VerificationSeverity.Warning,
        title: 'Inconsistent unlocks across obstacles',
        meaning: 'A cell has several obstacles (e.g. a Strength approach and an Intellect approach), but a given outcome opens the way on some and not others. A player who resolves the “wrong” way passes yet the path never opens — they get stuck.',
        fix: 'Attach the same unlock to every obstacle on the cell (all open the way, or none do).',
    },
    [VerificationCode.UnreachableNode]: {
        severity: VerificationSeverity.Warning,
        title: 'Cell can never be entered',
        meaning: 'No run ever reaches this cell, so its trial and rewards are never seen.',
        fix: 'Either connect it (unlock a path to it) or remove it if it is intentionally dead.',
    },
    [VerificationCode.UnreachablePanel]: {
        severity: VerificationSeverity.Warning,
        title: 'Panel shown only on a dead branch',
        meaning: 'A panel is referenced, but only on an outcome branch that no run ever reaches, so it never actually pops up.',
        fix: 'Bind the panel to a branch the player can reach, or remove it.',
    },
    [VerificationCode.IntendedEffectNeverFires]: {
        severity: VerificationSeverity.Warning,
        title: 'Authored effect never fires',
        meaning: 'An outcome carries effects (unlocks/locks/panels) on a branch that no run ever reaches, so those effects are dead.',
        fix: 'Move the effect to a reachable branch, or drop it.',
    },
    [VerificationCode.SelfLockContradiction]: {
        severity: VerificationSeverity.Warning,
        title: 'Locks and unlocks the same cell',
        meaning: 'One outcome both locks and unlocks the same cell. At runtime the unlock wins, so the lock is pointless.',
        fix: 'Remove whichever action is not intended.',
    },
    [VerificationCode.RetryFictionMismatch]: {
        severity: VerificationSeverity.Warning,
        title: 'Retry flag disagrees with the fiction',
        meaning: 'The obstacle’s failure fiction (what the text says happens on failure) implies one retry policy, but the retry flag is set the other way.',
        fix: 'Align the retry flag with the failure fiction (retryable vs. one-shot).',
    },
    [VerificationCode.ContentOnImpassableCell]: {
        severity: VerificationSeverity.Warning,
        title: 'Obstacle on a cell the game never reads',
        meaning: 'The cell is Empty (no type) or a wall, so the game engine skips it entirely — but it still carries an obstacle. The obstacle is dead, and clearing a route cell’s type this way usually severs the route.',
        fix: 'Give the cell a type again, or clear its leftover content.',
    },
    [VerificationCode.BrokenEventRef]: {
        severity: VerificationSeverity.Error,
        title: 'Cell attached to a deleted event',
        meaning: 'The cell references an event that was deleted (archived). Play still shows the trial’s stored texts, but the event behind them is gone — pressing Take a chance silently does nothing.',
        fix: 'Detach the obstacle from the cell, or restore the event from the Events tab’s archive and re-attach it.',
    },
    [VerificationCode.TrialOnStart]: {
        severity: VerificationSeverity.Error,
        title: 'Event on the start cell',
        meaning: 'The start (spawn) cell carries an event. The game engine cannot run a trial where the run begins — it would silently never fire.',
        fix: 'Move the event to another cell, or detach it from the start cell.',
    },
    [VerificationCode.OrphanPanel]: {
        severity: VerificationSeverity.Warning,
        title: 'Panel is never shown',
        meaning: 'A notification panel exists but no obstacle ever shows it — leftover content.',
        fix: 'Reference it from some outcome, or archive/delete it.',
    },
    [VerificationCode.VerificationTruncated]: {
        severity: VerificationSeverity.Info,
        title: 'Verification truncated',
        meaning: 'The level’s state space was too large to explore exhaustively, so solvability was sampled rather than proven. The static checks are still complete. (Rare — only levels that LOCK cells can trigger it.)',
        fix: 'No action needed. If you must have a proven verdict, simplify the locking logic.',
    },
    [VerificationCode.EmptyOutcome]: {
        severity: VerificationSeverity.Info,
        title: 'Outcome has no effects (retired)',
        meaning: 'Historic code. An outcome with no effects (e.g. a failure that just costs energy) is a normal, fully-supported pattern, so this is no longer reported.',
        fix: 'Nothing — kept only so old data does not break.',
        retired: true,
    },
};
// Generation-error causes: what actually hides behind a `(code: XXXXXX)`.
const GEN_ERROR_CAUSES = [
    {
        cause: 'Provider account out of credits',
        log: '“Your credit balance is too low…” (Anthropic) / “insufficient_quota” (OpenAI)',
        fix: 'Top up the provider account, or paste a funded key in API Keys.',
    },
    {
        cause: 'Rate / quota limit hit',
        log: 'HTTP 429 “rate_limit” or “quota exceeded”',
        fix: 'Wait and retry, or raise the plan limit on the provider.',
    },
    {
        cause: 'Invalid or revoked API key',
        log: 'HTTP 401 “invalid x-api-key” / “Incorrect API key”',
        fix: 'Replace the key in the API Keys dialog.',
    },
    {
        cause: 'Model returned malformed / empty output',
        log: '“returned no content” or a JSON parse error',
        fix: 'Retry; if it persists, check the prompt for that stage.',
    },
    {
        cause: 'Provider timeout or outage',
        log: 'HTTP 5xx / socket timeout',
        fix: 'Transient — retry later.',
    },
    {
        cause: 'Image model rejected the job (bad model id, unsupported parameter)',
        log: '“fal submit failed: 4xx …” or “fal job ended: FAILED … (model …)”',
        fix: 'Check the fal model id in API Keys → Models and retry. The editor asks the provider for PNG and converts to the game format (JPG / WebP cover) itself.',
    },
];
const SEVERITY_META = {
    [VerificationSeverity.Error]: { label: 'Error', icon: _jsx(FaExclamationCircle, {}), order: 0 },
    [VerificationSeverity.Warning]: { label: 'Warning', icon: _jsx(FaExclamationTriangle, {}), order: 1 },
    [VerificationSeverity.Info]: { label: 'Info', icon: _jsx(FaInfoCircle, {}), order: 2 },
};
/** Admin tool: find level maps that are not the size the game needs and rewrite
 *  them. The game lays cells on a fixed pitch over a padded grid and draws the
 *  picture at natural size, so an off-size map puts the cells off the picture. */
function MapSizesSection() {
    var _a, _b, _c, _d, _e, _f, _g;
    const [report, setReport] = useState(null);
    const [result, setResult] = useState(null);
    const [fixAll, setFixAll] = useState(null);
    const [busy, setBusy] = useState(false);
    // Which single level is being resized right now (its own row shows the spinner).
    const [busyLevel, setBusyLevel] = useState(null);
    const [error, setError] = useState(null);
    const load = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        setBusy(true);
        setError(null);
        setResult(null);
        try {
            setReport(yield getMapNormalizeReport());
        }
        catch (err) {
            setError(((_b = (_a = err === null || err === void 0 ? void 0 : err.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.message) || 'Could not read the level maps.');
        }
        finally {
            setBusy(false);
        }
    }), []);
    useEffect(() => { void load(); }, [load]);
    const run = () => __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        setBusy(true);
        setError(null);
        try {
            setResult(yield runMapNormalize());
            setReport(yield getMapNormalizeReport());
        }
        catch (err) {
            setError(((_b = (_a = err === null || err === void 0 ? void 0 : err.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.message) || 'Could not resize the maps.');
        }
        finally {
            setBusy(false);
        }
    });
    // Resize ONE level, then refresh the list so the row disappears on success.
    const runOne = (levelId) => __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        setBusyLevel(levelId);
        setError(null);
        setResult(null);
        try {
            setResult(yield runMapNormalize(levelId));
            setReport(yield getMapNormalizeReport());
        }
        catch (err) {
            setError(((_b = (_a = err === null || err === void 0 ? void 0 : err.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.message) || 'Could not resize this map.');
        }
        finally {
            setBusyLevel(null);
        }
    });
    const runAll = () => __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        setBusy(true);
        setError(null);
        try {
            setFixAll(yield runFixAll());
            setReport(yield getMapNormalizeReport());
        }
        catch (err) {
            setError(((_b = (_a = err === null || err === void 0 ? void 0 : err.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.message) || 'Could not run the fix.');
        }
        finally {
            setBusy(false);
        }
    });
    const offSize = (_a = report === null || report === void 0 ? void 0 : report.rows.filter(r => r.status === 'off-size')) !== null && _a !== void 0 ? _a : [];
    return (_jsxs("div", { className: "im-card", children: [_jsx("h2", { children: "Level map sizes" }), _jsxs("p", { className: "im-sub", children: ["The game draws the map at its natural size and lays the cells on a fixed pitch over a padded grid, so a background must be exactly", ' ', _jsxs("strong", { children: [(_b = report === null || report === void 0 ? void 0 : report.canonical) !== null && _b !== void 0 ? _b : MAP_IMAGE_PX, "\u00D7", (_c = report === null || report === void 0 ? void 0 : report.canonical) !== null && _c !== void 0 ? _c : MAP_IMAGE_PX] }), ". At any other size the cells land off the picture and the level looks broken. Resizing rescales the art \u2014 fine detail softens, but the level becomes playable."] }), error && _jsx("p", { className: "im-map-error", children: error }), report && (_jsxs("p", { className: "im-map-summary", children: [report.total, " levels scanned \u00B7 ", _jsx("strong", { children: report.offSize }), " need resizing"] })), offSize.length > 0 && (_jsx("ul", { className: "im-map-list", children: offSize.map(r => (_jsxs("li", { children: [_jsx("span", { className: "im-map-name", children: r.name }), _jsxs("span", { className: "im-map-size", children: [r.width, "\u00D7", r.height] }), _jsx("button", { className: "im-map-one", disabled: busy || busyLevel === r.levelId, onClick: () => runOne(r.levelId), children: busyLevel === r.levelId ? 'Resizing…' : 'Resize' })] }, r.levelId))) })), _jsxs("div", { className: "im-map-actions", children: [_jsx("button", { onClick: load, disabled: busy, children: "Rescan" }), _jsx("button", { className: "im-map-run", onClick: run, disabled: busy || offSize.length === 0, children: busy ? 'Working…' : `Resize ${offSize.length} map${offSize.length === 1 ? '' : 's'} to ${(_d = report === null || report === void 0 ? void 0 : report.canonical) !== null && _d !== void 0 ? _d : MAP_IMAGE_PX}×${(_e = report === null || report === void 0 ? void 0 : report.canonical) !== null && _e !== void 0 ? _e : MAP_IMAGE_PX}` })] }), result && (_jsxs("div", { className: "im-map-result", children: [result.resized.length > 0 && (_jsxs("p", { children: ["Resized ", result.resized.length, ": ", result.resized.map(r => `${r.name} (${r.from} → ${r.to})`).join(', ')] })), result.failed.length > 0 && (_jsxs("p", { className: "im-map-error", children: ["Failed ", result.failed.length, ": ", result.failed.map(f => `${f.name} — ${f.reason}`).join(', ')] })), result.resized.length === 0 && result.failed.length === 0 && _jsx("p", { children: "Nothing needed resizing." })] })), _jsxs("div", { className: "im-fixall", children: [_jsx("h3", { children: "Fix everything" }), _jsxs("p", { className: "im-sub", children: ["Run once after a deploy: brings every level in the database in line with the current rules \u2014 maps resized to ", (_f = report === null || report === void 0 ? void 0 : report.canonical) !== null && _f !== void 0 ? _f : MAP_IMAGE_PX, "\u00D7", (_g = report === null || report === void 0 ? void 0 : report.canonical) !== null && _g !== void 0 ? _g : MAP_IMAGE_PX, ", and cells typed \u201CEmpty\u201D marked as impassable everywhere. Safe to run twice."] }), _jsx("div", { className: "im-map-actions", children: _jsx("button", { className: "im-map-run", onClick: runAll, disabled: busy, children: busy ? 'Working…' : 'Fix all levels' }) }), fixAll && (_jsxs("div", { className: "im-map-result", children: [_jsxs("p", { children: ["Maps resized: ", fixAll.maps.resized.length, fixAll.maps.resized.length > 0 && ` — ${fixAll.maps.resized.join(', ')}`] }), _jsxs("p", { children: ["Empty cells marked: ", fixAll.emptyCellsFlagged] }), fixAll.maps.failed.length > 0 && (_jsxs("p", { className: "im-map-error", children: ["Failed: ", fixAll.maps.failed.map(f => `${f.name} — ${f.reason}`).join(', ')] }))] }))] })] }));
}
function InfoManager() {
    const [section, setSection] = useState('errorCodes');
    const rows = Object.entries(VERIFICATION_DOCS)
        .sort((a, b) => (a[1].retired ? 1 : 0) - (b[1].retired ? 1 : 0)
        || SEVERITY_META[a[1].severity].order - SEVERITY_META[b[1].severity].order
        || a[0].localeCompare(b[0]));
    return (_jsxs("div", { className: "info-manager", children: [_jsxs("div", { className: "im-subnav", children: [_jsx("button", { className: section === 'errorCodes' ? 'active' : '', onClick: () => setSection('errorCodes'), children: "Error codes" }), _jsx("button", { className: section === 'genErrors' ? 'active' : '', onClick: () => setSection('genErrors'), children: "Generation errors" }), _jsx("button", { className: section === 'maps' ? 'active' : '', onClick: () => setSection('maps'), children: "Map sizes" })] }), section === 'maps' && _jsx(MapSizesSection, {}), section === 'errorCodes' && (_jsxs("div", { className: "im-card", children: [_jsx("h2", { children: "Level verification codes" }), _jsxs("p", { className: "im-sub", children: ["Codes the level auto-checker can report. ", _jsx("strong", { children: "Errors" }), " block a level from being solvable or consistent and must be fixed; ", _jsx("strong", { children: "warnings" }), " are suspicious but not proven fatal; ", _jsx("strong", { children: "info" }), " is advisory."] }), _jsx("div", { className: "im-codes", children: rows.map(([code, doc]) => (_jsxs("div", { className: `im-code sev-${doc.severity}${doc.retired ? ' retired' : ''}`, children: [_jsxs("div", { className: "im-code-head", children: [_jsxs("span", { className: `im-sev sev-${doc.severity}`, children: [SEVERITY_META[doc.severity].icon, SEVERITY_META[doc.severity].label] }), _jsx("span", { className: "im-code-title", children: doc.title }), _jsx("code", { className: "im-code-id", children: code })] }), _jsxs("div", { className: "im-code-body", children: [_jsx("p", { children: doc.meaning }), _jsxs("p", { className: "im-fix", children: [_jsx("span", { children: "Fix" }), " ", doc.fix] })] })] }, code))) })] })), section === 'genErrors' && (_jsxs("div", { className: "im-card", children: [_jsxs("h2", { children: ["AI generation error codes ", _jsx("code", { className: "im-code-id", children: "(code: XXXXXX)" })] }), _jsxs("p", { className: "im-sub", children: ["When an AI job (story, translation, image) fails, the app shows a generic message plus a six-character code, e.g. ", _jsx("code", { className: "im-code-id", children: "(code: 2JCCNC)" }), ". That code is", _jsx("strong", { children: " not a category" }), " \u2014 it is a one-off ", _jsx("strong", { children: "log reference" }), ". The real provider error often carries billing text, so it is deliberately hidden from the screen and written only to the backend log."] }), _jsxs("div", { className: "im-loglead", children: [_jsx(FaTerminal, {}), _jsxs("div", { children: [_jsxs("div", { children: ["To read the real cause, find this line in the backend log (the terminal running ", _jsx("code", { children: "npm run dev:backend" }), ", or the server logs in production):"] }), _jsx("code", { className: "im-logline", children: "[gen-error XXXXXX] <the full provider error>" }), _jsxs("div", { className: "im-note", children: ["The ", _jsx("code", { children: "XXXXXX" }), " matches the code shown to the user exactly."] })] })] }), _jsx("h3", { className: "im-h3", children: "Common causes" }), _jsxs("div", { className: "im-causes", children: [_jsxs("div", { className: "im-cause im-cause-head", children: [_jsx("span", { children: "Cause" }), _jsx("span", { children: "Log looks like" }), _jsx("span", { children: "What to do" })] }), GEN_ERROR_CAUSES.map((c) => (_jsxs("div", { className: "im-cause", children: [_jsx("span", { className: "im-cause-name", children: c.cause }), _jsx("span", { className: "im-cause-log", children: c.log }), _jsx("span", { children: c.fix })] }, c.cause)))] })] }))] }));
}
export default InfoManager;
